/**
 * Pure logic behind the workspace data grid (T-0140).
 *
 * Everything here is a plain function with no React and no DOM writes so
 * the behaviour that can break silently (range selection, tri-state
 * derivation, keyboard mapping and its suppression while typing) is unit
 * tested without rendering a grid. `data-grid.tsx` is the only consumer
 * that wires these to events.
 *
 * Behavioural reference: `docs/requirement-library/prototype/selection.js`
 * (`toggleCheck`, `onKey`).
 */

export type TriState = 'on' | 'off' | 'mixed';

/**
 * Header checkbox state for a set of selectable ids.
 * No selectable rows -> 'off' (never 'on' for an empty set).
 */
export function deriveTriState(
  selectableIds: ReadonlyArray<string>,
  selected: ReadonlySet<string>,
): TriState {
  if (selectableIds.length === 0) return 'off';
  let count = 0;
  for (const id of selectableIds) if (selected.has(id)) count += 1;
  if (count === 0) return 'off';
  return count === selectableIds.length ? 'on' : 'mixed';
}

/**
 * Select-all click. 'on' clears the selectable ids; 'off' and 'mixed'
 * select all of them. Ids outside `selectableIds` (for example rows
 * hidden by a filter) are left as they were.
 */
export function toggleAll(
  selectableIds: ReadonlyArray<string>,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  if (deriveTriState(selectableIds, selected) === 'on') {
    for (const id of selectableIds) next.delete(id);
  } else {
    for (const id of selectableIds) next.add(id);
  }
  return next;
}

export interface ToggleSelectionInput {
  /** Row ids in display order (group headers excluded). */
  orderedIds: ReadonlyArray<string>;
  selected: ReadonlySet<string>;
  /** The row being toggled. */
  targetId: string;
  /** The row toggled last time; the other end of a shift range. */
  anchorId: string | null;
  shift: boolean;
  /** Rows that cannot change selection (locked checkbox). */
  isLocked?: (id: string) => boolean;
}

/**
 * Toggle one row, or with shift held apply the target row's NEW state to
 * every row between the anchor and the target (inclusive). A shift-click
 * with no usable anchor degrades to a plain toggle. Locked rows are
 * never changed.
 */
export function toggleSelection({
  orderedIds,
  selected,
  targetId,
  anchorId,
  shift,
  isLocked,
}: ToggleSelectionInput): Set<string> {
  const next = new Set(selected);
  if (isLocked?.(targetId)) return next;
  const turnOn = !selected.has(targetId);
  const apply = (id: string) => {
    if (isLocked?.(id)) return;
    if (turnOn) next.add(id);
    else next.delete(id);
  };
  if (shift && anchorId != null) {
    const a = orderedIds.indexOf(anchorId);
    const b = orderedIds.indexOf(targetId);
    if (a >= 0 && b >= 0) {
      for (let i = Math.min(a, b); i <= Math.max(a, b); i += 1) apply(orderedIds[i]!);
      return next;
    }
  }
  apply(targetId);
  return next;
}

/** Next focused id when moving up or down; clamps at both ends. */
export function moveFocus(
  orderedIds: ReadonlyArray<string>,
  focusedId: string | null,
  delta: 1 | -1,
): string | null {
  if (orderedIds.length === 0) return null;
  const i = focusedId == null ? -1 : orderedIds.indexOf(focusedId);
  if (i < 0) return delta > 0 ? orderedIds[0]! : orderedIds[orderedIds.length - 1]!;
  const j = Math.min(Math.max(i + delta, 0), orderedIds.length - 1);
  return orderedIds[j]!;
}

export type GridKeyAction =
  | { type: 'move'; delta: 1 | -1 }
  | { type: 'open' }
  | { type: 'toggle'; shift: boolean }
  | { type: 'edit' };

export interface GridKeyEventLike {
  key: string;
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}

/**
 * Map a key press to a grid action. Returns null for keys the grid does
 * not own, when a modifier is held (so Cmd/Ctrl+K reaches the command
 * palette), or when `typing` is true (focus is in an input, textarea,
 * select or contenteditable).
 */
export function gridKeyAction(
  e: GridKeyEventLike,
  ctx: { typing: boolean; hasFocus: boolean },
): GridKeyAction | null {
  if (ctx.typing) return null;
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  switch (e.key) {
    case 'ArrowDown':
    case 'j':
      return { type: 'move', delta: 1 };
    case 'ArrowUp':
    case 'k':
      return { type: 'move', delta: -1 };
    default:
      break;
  }
  if (!ctx.hasFocus) return null;
  switch (e.key) {
    case 'Enter':
      return { type: 'open' };
    case 'x':
    case ' ':
      return { type: 'toggle', shift: Boolean(e.shiftKey) };
    case 'e':
      return { type: 'edit' };
    default:
      return null;
  }
}

/** True when the event target is somewhere the user types text. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).tagName !== 'string') return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable === true || el.getAttribute?.('contenteditable') === 'true';
}

export type InlineEditKeyAction = 'commit' | 'cancel' | null;

/** Enter commits, Shift+Enter inserts a newline (null), Escape cancels. */
export function inlineEditKeyAction(e: GridKeyEventLike): InlineEditKeyAction {
  if (e.key === 'Enter' && !e.shiftKey) return 'commit';
  if (e.key === 'Escape') return 'cancel';
  return null;
}

export type GridItem<T> =
  | { kind: 'group'; key: string; count: number }
  | { kind: 'row'; row: T; id: string; rowIndex: number }
  | { kind: 'footer'; key: string };

/**
 * Flatten rows into the virtualised item list, inserting a group header
 * each time `groupBy` changes. Rows must already be sorted by group.
 * With `groupFooters`, a footer item closes every group (for example an
 * "add a row to this group" control); footers are not rows, so they are
 * left out of `orderedIds` and keyboard movement skips them.
 */
export function buildGridItems<T>(
  rows: ReadonlyArray<T>,
  getRowId: (row: T) => string,
  groupBy?: (row: T) => string,
  groupFooters = false,
): { items: GridItem<T>[]; orderedIds: string[]; groupIndexes: number[] } {
  const items: GridItem<T>[] = [];
  const orderedIds: string[] = [];
  const groupIndexes: number[] = [];
  let current: { kind: 'group'; key: string; count: number } | null = null;
  const closeGroup = () => {
    if (groupFooters && current) items.push({ kind: 'footer', key: current.key });
  };
  rows.forEach((row, rowIndex) => {
    if (groupBy) {
      const key = groupBy(row);
      if (!current || current.key !== key) {
        closeGroup();
        current = { kind: 'group', key, count: 0 };
        groupIndexes.push(items.length);
        items.push(current);
      }
      current.count += 1;
    }
    const id = getRowId(row);
    orderedIds.push(id);
    items.push({ kind: 'row', row, id, rowIndex });
  });
  closeGroup();
  return { items, orderedIds, groupIndexes };
}

/**
 * Index of the group header that should be pinned for a given first
 * visible index: the last group at or before it. -1 when there is none.
 */
export function activeGroupIndex(groupIndexes: ReadonlyArray<number>, startIndex: number): number {
  let active = -1;
  for (const i of groupIndexes) {
    if (i <= startIndex) active = i;
    else break;
  }
  return active;
}
